import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { LearnerFoot } from "@/components/session/SessionLearner";
import { SessionReadFailure } from "./SessionReadFailure";
import type { FollowUpRead, FollowUpSession, SessionWorkspace } from "@/lib/api/sessionPortal";
import { classTitle } from "@/lib/class-name";
import { formatInCourseZone, sessionChangeWords } from "@/lib/session-portal";
import { neighbourShortcut } from "@/lib/session-navigation";

const shortName = (session: Pick<FollowUpSession, "session_no" | "label">) =>
  session.session_no == null ? session.label?.trim() || "Session" : `Session ${session.session_no}`;

/** A cancelled session is one line in time order, never the page. */
function CancelledLine({ session }: { session: FollowUpSession }) {
  const when = formatInCourseZone(session.starts_at, session.timezone, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  return <p data-session-cancelled-line className="flex flex-wrap items-center gap-x-1 px-1 text-ui text-muted-foreground">
    <Link to={session.destination.href} className="inline-flex min-h-11 items-center underline-offset-4 hover:underline">{shortName(session)}</Link>{` · ${when} · Cancelled. No class meets.`}
  </p>;
}

const PLAN_WORD = { none: "No plan yet", not_published: "Plan not published", published: "Published" } as const;

/** The plan word a scheduled session carries, as the team reads it. */
function planWords(plan: FollowUpRead<{ word: keyof typeof PLAN_WORD } | { first_line: string | null }>): string | null {
  if (plan.state !== "ready") return null;
  return "word" in plan.value ? PLAN_WORD[plan.value.word] : plan.value.first_line;
}

/**
 * The session page's foot. The team's reads forward: the last session that
 * met, the next scheduled, and the cancelled between (`continuation.foot`);
 * the learner's foot is its own page's. These reads fail independently. An
 * unavailable read never establishes an edge or zero.
 */
export function SessionContinuation({ workspace, onSelectSession, cancelledInFoot = true, onRetry }: {
  workspace: SessionWorkspace;
  onSelectSession?: (id: string) => void;
  /** A cancelled session shows once per page: after Follow up when it has a box, else here. */
  cancelledInFoot?: boolean;
  onRetry?: () => void;
}) {
  const navigate = useNavigate();
  const continuationRef = useRef<HTMLElement>(null);
  const neighbours = workspace.continuation?.neighbours;
  const { course } = workspace;
  const foot = workspace.continuation?.foot;
  const previous = foot?.previous.state === "ready" ? foot.previous.value : null;
  const next = foot?.next_scheduled.state === "ready" ? foot.next_scheduled.value : null;
  const cancelled = cancelledInFoot && foot?.cancelled.state === "ready" ? foot.cancelled.value : [];
  const nameOf = (session: FollowUpSession | null) => session
    ? classTitle({ session_no: session.session_no, label: session.label, starts_at: session.starts_at, session_date: null }, session.timezone)
    : "";
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const direction = neighbourShortcut(event);
      if (!direction || neighbours?.state !== "ready" || continuationRef.current?.closest("[hidden]")) return;
      const session = neighbours.value[direction];
      if (!session) return;
      event.preventDefault();
      if (onSelectSession) onSelectSession(session.id);
      else navigate(session.destination.href);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [neighbours, navigate, onSelectSession]);
  const terminal = "No further session is scheduled.";
  if (!workspace.viewer.capacities.includes("teaching")) return <LearnerFoot workspace={workspace} onSelectSession={onSelectSession} continuationRef={continuationRef} cancelledInFoot={cancelledInFoot} onRetry={onRetry} />;
  const when = (session: FollowUpSession) => formatInCourseZone(session.starts_at, session.timezone, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", ...(session.timezone !== course.timezone ? { timeZoneName: "short" as const } : {}) });
  return <section data-session-continuation aria-label="Session continuation" className="grid gap-2 border-t border-rule py-3">
    {cancelled.map(session => <CancelledLine key={session.id} session={session} />)}
    {cancelledInFoot && foot?.cancelled.state === "unavailable" && <SessionReadFailure part="cancelled sessions" retry={onRetry} />}
    <nav ref={continuationRef} aria-label="Session navigation" className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 text-ui">
      {([["Previous", previous], ["Next scheduled", next]] as const).map(([direction, session]) => {
        const read = direction === "Previous" ? foot?.previous : foot?.next_scheduled;
        if (!read || read.state === "unavailable") return <SessionReadFailure key={direction} part={direction === "Previous" ? "previous session" : "next scheduled session"} retry={onRetry} />;
        const plan = session && direction === "Next scheduled" && "plan" in session ? planWords(session.plan) : null;
        const word = session && (session.latest_change ? sessionChangeWords(session.latest_change) : "status" in session && session.status ? (session.status === "held" ? "Held" : session.status === "ended" ? "Ended, not held" : null) : null);
        const words = session && `${direction}: ${nameOf(session)}, ${when(session)}${word ? ` · ${word}` : ""}${plan ? ` · ${plan}` : ""}`;
        return !session ? <span key={direction} className="inline-flex min-h-11 items-center text-meta text-muted-foreground">{direction === "Previous" ? "This is the first scheduled session." : terminal}</span>
          : onSelectSession ? <Button key={direction} variant="subtle" className="h-auto min-h-11 max-w-full whitespace-normal text-start" onClick={() => onSelectSession(session.id)}>{words}</Button>
          : <Link key={direction} className="block min-h-11 content-center text-primary underline-offset-4 hover:underline" to={session.destination.href}>{words}</Link>;
      })}
    </nav>
  </section>;
}
